import { useState, forwardRef, useRef, useEffect } from 'react';
import './taskCard.scss';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { taskCardI18n } from './i18n';
import TextArea from '../../components/TextArea/TextArea.jsx';
import { formatDate } from '../../utils/formatDate.js';
import Tag from '../Tag/Tag.jsx';
import TaskConfirmation from '../TaskConfirmation/TaskConfirmation.jsx';
import {
  getStoredComments,
  removeStoredComment,
  storeComment,
} from 'utils/commentHistory';

const WORKFLOW_STATUS = {
  IN_PROGRESS: 1,
  APPROVED: 2,
  REJECTED: 3,
  FINALIZED: 4,
};

const parseWorkflowStatus = (value) => {
  if (typeof value === 'number') return value;
  if (typeof value !== 'string') return null;

  const normalized = value.trim().toLowerCase();
  if (normalized === 'inprogress') return WORKFLOW_STATUS.IN_PROGRESS;
  if (normalized === 'approved') return WORKFLOW_STATUS.APPROVED;
  if (normalized === 'rejected') return WORKFLOW_STATUS.REJECTED;
  if (normalized === 'finalized') return WORKFLOW_STATUS.FINALIZED;

  const asNumber = Number(value);
  return Number.isFinite(asNumber) ? asNumber : null;
};

const TaskCard = forwardRef(
  (
    {
      DesignSystemComponents,
      task,
      onView,
      onEdit,
      onSave,
      onAddComment,
      onOpenAttachment,
      onOpenCommentDocument,
      onApprove,
      onReject,
      isLoadingTaskApproved = false,
      workflowInstance = null,
      onViewWorkflow,
      isWorkflowFlowLoading = false,
      defaultExpanded = true,
      canManageTask = true,
      canEditTask = true,
      canPublishComment = true,
      canAttachFile = true,
    },
    ref
  ) => {
    const { Card, Badge, IconButton, Input, SvgIcon, Button } =
      DesignSystemComponents;
    const { t } = useScopedTranslation(taskCardI18n);
    const [expanded, setExpanded] = useState(defaultExpanded);
    const [newComment, setNewComment] = useState('');
    const [confirmationMode, setConfirmationMode] = useState(null);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isLoadingComment, setIsLoadingComment] = useState(false);
    const fileInputRef = useRef(null);
    const [recentComments, setRecentComments] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);

    const comments = task.comments || task.comentarios || [];
    const handleToggle = () => setExpanded((prev) => !prev);

    const handleSubmitComment = async () => {
      const text = newComment.trim();

      if (!text) {
        alert(t('tc_alert_empty_comment'));
        return;
      }
      if (!onAddComment) return;
      if (!task?.id) {
        alert(t('tc_alert_no_task'));
        return;
      }
      if (isLoadingComment) return;

      try {
        setIsLoadingComment(true);

        await onAddComment(task.id, text, selectedFiles);

        storeComment(text);
        setRecentComments(getStoredComments());

        setNewComment('');
        setSelectedFiles([]);
        if (fileInputRef.current) fileInputRef.current.value = '';
      } catch (err) {
        console.error('Failed to add comment:', err);
      } finally {
        setIsLoadingComment(false);
      }
    };

    const assignedName =
      task?.abogadosAsignados?.length > 0
        ? task?.abogadosAsignados[0]?.name
        : t('tc_unassigned_user');

    const title = task.title || task.titulo || t('tc_untitled_task');
    const nameFile = task.nameFile || '';

    const status = task.status || task.estado || null;
    const priority = task.priority || task.prioridad || null;
    const dueDate = task.due || task.fechaVencimiento || null;
    const createDate =
      task.createDate || task.createAt || task.fechaCreacion || null;
    const creator =
      task.creadorName ||
      task.creatorName ||
      task.creator ||
      task.creadorNombre ||
      null;

    const getStatusBadge = (status) => {
      const normalized = (status || '').toLowerCase();

      if (['in_progress', 'en progreso'].includes(normalized)) {
        return (
          <Badge text={t('tc_status_in_progress')} color='warning' size='sm' />
        );
      }
      if (['unassigned', 'sin asignar'].includes(normalized)) {
        return (
          <Badge text={t('tc_status_unassigned')} color='info' size='sm' />
        );
      }
      if (['approved', 'aprobada'].includes(normalized)) {
        return (
          <Badge text={t('tc_status_approved')} color='success' size='sm' />
        );
      }
      if (['rejected', 'rechazada'].includes(normalized)) {
        return <Badge text={t('tc_status_rejected')} color='error' size='sm' />;
      }
      return (
        <Badge
          text={status || t('tc_status_default')}
          color='default'
          size='sm'
        />
      );
    };

    const getPriorityBadge = (prioridad) => {
      switch (prioridad?.toLowerCase()) {
        case 'alta':
          return <Badge text={t('tc_priority_high')} color='error' size='sm' />;
        case 'media':
          return (
            <Badge text={t('tc_priority_medium')} color='warning' size='sm' />
          );
        case 'baja':
          return <Badge text={t('tc_priority_low')} color='info' size='sm' />;
        default:
          return (
            <Badge
              text={prioridad || t('tc_priority_default')}
              color='info'
              size='sm'
            />
          );
      }
    };

    const getWorkflowBadge = (workflow) => {
      if (!workflow) return null;

      const resolvedStatus =
        typeof workflow.parsedStatus === 'number'
          ? workflow.parsedStatus
          : parseWorkflowStatus(workflow.status);

      if (resolvedStatus === WORKFLOW_STATUS.IN_PROGRESS) {
        return (
          <Badge text={t('tc_wf_in_progress')} color='warning' size='sm' />
        );
      }
      if (resolvedStatus === WORKFLOW_STATUS.APPROVED) {
        return <Badge text={t('tc_wf_approved')} color='success' size='sm' />;
      }
      if (resolvedStatus === WORKFLOW_STATUS.REJECTED) {
        return <Badge text={t('tc_wf_rejected')} color='error' size='sm' />;
      }
      if (resolvedStatus === WORKFLOW_STATUS.FINALIZED) {
        return <Badge text={t('tc_wf_finalized')} color='info' size='sm' />;
      }
      return <Badge text={t('tc_wf_active')} color='light' size='sm' />;
    };

    useEffect(() => {
      if (expanded) {
        setRecentComments(getStoredComments());
      }
    }, [expanded]);

    if (!Card) return null;

    return (
      <div ref={ref} className='card mb-3'>
        <div className='p-3'>
          <div className='mb-3'>
            <div className='d-flex justify-content-between align-items-start'>
              <div>
                <p className='caption fw-bold mb-0'>{assignedName}</p>
                <div className='d-flex flex-wrap'>
                  <p className='caption font-light mb-0'>
                    {creator
                      ? t('tc_created_by', { name: creator })
                      : t('tc_no_creator')}
                  </p>
                  <p className='caption color-gray-600 mb-0 px-2'>•</p>
                  <p className='caption font-light mb-0'>
                    {t('tc_task_number', { id: task.id })}
                  </p>
                </div>
                <div className='d-flex flex-wrap'>
                  <p className='caption font-light mb-0'>
                    {createDate
                      ? t('tc_created_date', { date: formatDate(createDate) })
                      : t('tc_no_date')}
                  </p>
                  <p className='caption color-gray-600 mb-0 px-2'>•</p>
                  <p className='caption font-light mb-0'>
                    {dueDate
                      ? t('tc_due_date', { date: formatDate(dueDate) })
                      : t('tc_no_date')}
                  </p>
                </div>
              </div>
              <div className='d-flex align-items-end flex-column gap-3'>
                {status === 'in_progress' && (
                  <div className='d-flex align-items-center gap-2'>
                    <Tag
                      label={
                        isLoadingTaskApproved
                          ? t('tc_rejecting')
                          : t('tc_reject')
                      }
                      icon='circleX'
                      disabled={isLoadingTaskApproved || !canManageTask}
                      iconStroke='var(--error-500)'
                      onClick={() => !isLoadingTaskApproved && onReject?.(task)}
                      color='danger'
                    />
                    <Tag
                      label={
                        isLoadingTaskApproved
                          ? t('tc_approving')
                          : t('tc_approve')
                      }
                      icon='circleCheck'
                      disabled={isLoadingTaskApproved || !canManageTask}
                      iconStroke='var(--success-500)'
                      onClick={() =>
                        !isLoadingTaskApproved && onApprove?.(task)
                      }
                      color='success'
                    />
                  </div>
                )}
                <div className='d-flex gap-3'>
                  <div className='d-flex gap-2 flex-wrap align-items-center justify-content-end'>
                    {status && getStatusBadge(status)}
                    {priority && getPriorityBadge(priority)}
                    {workflowInstance && getWorkflowBadge(workflowInstance)}
                  </div>
                  <div className='d-flex align-items-center gap-2'>
                    {workflowInstance && onViewWorkflow && (
                      <button
                        type='button'
                        className='task-card-workflow-link-btn'
                        onClick={onViewWorkflow}
                        disabled={isWorkflowFlowLoading}
                      >
                        <SvgIcon
                          name='eye'
                          size='14px'
                          stroke='var(--primary-500)'
                        />
                        {t('tc_view_flow')}
                      </button>
                    )}
                    {onEdit && canEditTask && (
                      <IconButton
                        ariaLabel={t('tc_aria_edit')}
                        icon='pencil'
                        size='sm'
                        onClick={onEdit}
                        iconStroke='var(--primary-500)'
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Header */}
          <div>
            <div>
              <h3 className='fw-semibold  mb-1'>{title}</h3>
              <div className='row'>
                <div className='col-8'>
                  <p className='description mb-0 me-2'>
                    {task.description ||
                      task.descripcion ||
                      t('tc_no_description')}
                  </p>
                </div>
                <div className='col-4'>
                  <div className='d-flex align-items-center justify-content-end'>
                    {nameFile && (
                      <Button
                        iconEnd='paperclip'
                        label={nameFile}
                        size='sm'
                        variant='naked'
                        onClick={() => onOpenAttachment?.(task)}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <input
          type='file'
          ref={fileInputRef}
          style={{ display: 'none' }}
          multiple
          onChange={(e) => {
            const files = Array.from(e.target.files || []);
            if (files.length > 0) {
              setSelectedFiles((prev) => [...prev, ...files]);
            }
            e.target.value = null;
          }}
        />

        {/* Comentarios */}
        <div
          className={`mt-2 task-card-comments-wrapper ${
            expanded ? 'task-card-comments-wrapper--expanded' : ''
          }`}
        >
          <button
            type='button'
            className='w-100 d-flex align-items-center justify-content-between px-3 py-2 task-card-comments-toggle'
            onClick={handleToggle}
          >
            <div className='d-flex align-items-center gap-2'>
              <span className='caption '>{t('tc_comments')}</span>
              <Badge text={comments.length} color='light' size='sm' />
            </div>
            <span
              className={`task-card-comments-chevron ${
                expanded ? 'task-card-comments-chevron--expanded' : ''
              }`}
            >
              <SvgIcon name='chevronDown' size='1.3rem' stroke='var(--dark)' />
            </span>
          </button>

          {expanded && (
            <div className='task-comment-container'>
              <div className='d-flex flex-column gap-3 mb-3'>
                {comments.length > 0 ? (
                  comments.map((c) => (
                    <div
                      key={c.id || c.fechaCreacion}
                      className='p-3 task-card-comment'
                    >
                      <div className='d-flex align-items-center gap-2 mb-1'>
                        <span className='caption dark fw-semibold'>
                          {c.author ||
                            c.creadorNombre ||
                            c.creatorName ||
                            'User'}
                        </span>
                        {c.fechaCreacion && (
                          <span className='font-light caption'>
                            • {formatDate(c.fechaCreacion)}
                          </span>
                        )}
                        {c.create_At && (
                          <span className='font-light caption'>
                            • {formatDate(c.create_At)}
                          </span>
                        )}
                      </div>
                      <span className='font-light caption'>
                        {c.contenido || c.content}
                      </span>
                      {/* 1 archivo */}
                      {(c.urlAdjunto || c.urlAttachment) && (
                        <div
                          className='d-flex align-items-center mt-1 cursor-pointer'
                          onClick={() => onOpenCommentDocument?.(c)}
                        >
                          <SvgIcon
                            name='fileText'
                            size='1rem'
                            className='me-2'
                            stroke='var(--primary)'
                          />
                          <span className='caption text-primary text-decoration-underline'>
                            {c.nameAttachment ||
                              c.nombreAdjunto ||
                              c.urlAttachment}
                          </span>
                        </div>
                      )}

                      {/* N archivos */}
                      {c.documents?.length > 0 &&
                        c.documents.map((doc) => (
                          <div
                            key={doc.id}
                            className='d-flex align-items-center mt-1 cursor-pointer'
                            onClick={() => onOpenCommentDocument?.(doc)}
                          >
                            <SvgIcon
                              name='fileText'
                              size='1rem'
                              className='me-2'
                              stroke='var(--primary)'
                            />
                            <span className='caption text-primary text-decoration-underline'>
                              {doc.nameAttachment}
                            </span>
                          </div>
                        ))}
                      {c.commentTaskDocument?.length > 0 &&
                        c.commentTaskDocument.map((doc) => (
                          <div
                            key={doc.id}
                            className='d-flex align-items-center mt-1 cursor-pointer'
                            onClick={() => onOpenCommentDocument?.(doc)}
                          >
                            <SvgIcon
                              name='fileText'
                              size='1rem'
                              className='me-2'
                              stroke='var(--primary)'
                            />
                            <span className='caption text-primary text-decoration-underline'>
                              {doc.nameAttachment}
                            </span>
                          </div>
                        ))}
                    </div>
                  ))
                ) : (
                  <div className='font-light small color-gray-600'>
                    {t('tc_no_comments')}
                  </div>
                )}
              </div>

              {/* Nuevo comentario */}
              {Input && onAddComment && (
                <div className='d-flex  flex-column task-card-new-comment'>
                  <div className='position-relative'>
                    <div className='d-flex'>
                      <TextArea
                        placeholder={t('tc_comment_placeholder')}
                        value={newComment}
                        onChange={(e) => {
                          setNewComment(e.target.value);
                          setShowSuggestions(true);
                        }}
                        onFocus={() => setShowSuggestions(true)}
                        onBlur={() => {
                          setTimeout(() => setShowSuggestions(false), 150);
                        }}
                        rows={1}
                        autoGrow
                        showCount={false}
                        endIcon='send'
                        onEndIconClick={handleSubmitComment}
                        EndIconButton={IconButton}
                        className='flex-grow-1 me-2'
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSubmitComment();
                          }
                        }}
                        disabled={isLoadingComment || !canPublishComment}
                      />
                      <div style={{ marginBottom: '0.8125rem' }}>
                        <IconButton
                          ariaLabel={t('tc_aria_attach')}
                          icon='paperclip'
                          size='lg'
                          fill='var(--font-light)'
                          onClick={() => fileInputRef.current?.click()}
                          disabled={!canAttachFile}
                        />
                      </div>
                    </div>

                    {showSuggestions && recentComments.length > 0 && (
                      <>
                        <div className='task-comment-suggestions-arrow' />
                        <div className='task-comment-suggestions position-absolute'>
                          {recentComments.map((comment, idx) => (
                            <button
                              key={idx}
                              type='button'
                              className='task-comment-suggestion d-flex align-items-center justify-content-between'
                              onClick={() => {
                                setNewComment(comment);
                                setShowSuggestions(false);
                              }}
                            >
                              {comment}
                              <IconButton
                                ariaLabel={t('tc_aria_remove_suggestion')}
                                icon='close'
                                size='sm'
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeStoredComment(comment);
                                  setRecentComments(getStoredComments());
                                }}
                                iconStroke='var(--primary-500)'
                                className='ms-3'
                              />
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>

                  {selectedFiles.length > 0 && (
                    <div className='mb-2'>
                      {selectedFiles.map((f, idx) => (
                        <div
                          key={idx}
                          className='caption font-light d-flex align-items-center'
                        >
                          <SvgIcon
                            name='paperclip'
                            size='1rem'
                            className='me-2'
                            stroke='var(--primary)'
                          />
                          {f.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        <TaskConfirmation
          isOpen={!!confirmationMode}
          mode={confirmationMode === 'reject' ? 'reject' : 'approve'}
          onConfirm={() => {
            if (!confirmationMode || !onSave) {
              setConfirmationMode(null);
              return;
            }
            const approve = confirmationMode === 'approve';
            onSave(approve, task);
            setConfirmationMode(null);
          }}
          onCancel={() => setConfirmationMode(null)}
        />
      </div>
    );
  }
);

TaskCard.displayName = 'TaskCard';

export default TaskCard;
